import { authHeaders } from '../api.js';
import { liveUsagePageSchema, type LiveUsagePage } from '@ocnexus/contracts';
export type { LiveUsagePage } from '@ocnexus/contracts';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';
export type LiveSessionView = { id: string; syncId: string; repository: string; branch: string; state: string;
  budgetLimitMicros: number; spentMicros: number; reservedMicros: number; findingCount: number; maximumTopUpMicros: number };
export type LiveEvent = { cursor: string; kind: 'user_text' | 'assistant_text' | 'tool_progress' | 'turn_state' | 'budget';
  text: string; turnId: string | null };
type JsonObject = Record<string, unknown>;
function record(value: unknown): JsonObject | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as JsonObject : null;
}
function integer(value: unknown): value is number { return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0; }
export function decodeLiveSession(value: unknown): LiveSessionView | null {
  const row = record(value);
  if (!row || !['id','syncId','repository','branch','state'].every((key: string) => typeof row[key] === 'string')
    || !['budgetLimitMicros','spentMicros','reservedMicros','findingCount'].every((key: string) => integer(row[key]))
    || row.maximumTopUpMicros !== undefined && !integer(row.maximumTopUpMicros)
    || !['provisioning','ready','active','budget_exhausted','cancel_requested','failed'].includes(row.state as string)
    || (row.spentMicros as number) + (row.reservedMicros as number) > (row.budgetLimitMicros as number)) return null;
  return { id: row.id as string, syncId: row.syncId as string, repository: row.repository as string,
    branch: row.branch as string, state: row.state as string, budgetLimitMicros: row.budgetLimitMicros as number,
    spentMicros: row.spentMicros as number, reservedMicros: row.reservedMicros as number, findingCount: row.findingCount as number,
    maximumTopUpMicros: row.maximumTopUpMicros ?? 0 };
}
function cursor(value: unknown): value is string {
  return typeof value === 'string' && /^(0|[1-9][0-9]{0,18})$/.test(value) && BigInt(value) <= 9223372036854775807n;
}
export function decodeLiveEvents(value: unknown): LiveEvent[] | null {
  if (!Array.isArray(value) || value.length > 200) return null;
  const events: LiveEvent[] = [];
  for (const item of value) {
    const row = record(item);
    if (!row || !cursor(row.cursor) || typeof row.text !== 'string' || row.text.length > 64000
      || !['user_text','assistant_text','tool_progress','turn_state','budget'].includes(row.kind as string)
      || !(row.turnId === null || typeof row.turnId === 'string')) return null;
    events.push({ cursor: row.cursor, kind: row.kind as LiveEvent['kind'], text: row.text, turnId: row.turnId });
  }
  return events;
}
/** Duplicate event delivery never duplicates a bubble; conflicting cursor content is refused. */
export function mergeLiveEvents(previous: readonly LiveEvent[], incoming: readonly LiveEvent[]): LiveEvent[] {
  const map = new Map<string, LiveEvent>(previous.map((event: LiveEvent) => [event.cursor, event]));
  for (const event of incoming) {
    const prior = map.get(event.cursor);
    if (prior && JSON.stringify(prior) !== JSON.stringify(event)) throw new Error('event_conflict');
    map.set(event.cursor, event);
  }
  return [...map.values()].sort((a: LiveEvent, b: LiveEvent) => BigInt(a.cursor) < BigInt(b.cursor) ? -1 : BigInt(a.cursor) > BigInt(b.cursor) ? 1 : 0);
}
export function dollarsToMicros(value: string): number | null {
  if (!/^(0|[1-9][0-9]{0,8})(\.[0-9]{1,6})?$/.test(value)) return null;
  const amount = Math.round(Number(value) * 1000000);
  return Number.isSafeInteger(amount) && amount > 0 ? amount : null;
}
function url(chat: string, suffix = ''): string {
  return `${API_BASE}/api/subscriber-chat/chats/${encodeURIComponent(chat)}/live-codex${suffix}`;
}
async function request(chat: string, suffix: string, method: string, data?: unknown, signal?: AbortSignal): Promise<{ status: number; body: JsonObject }> {
  const headers = await authHeaders();
  if (!headers.Authorization) return { status: 401, body: { error: 'unauthorized' } };
  const response = await fetch(url(chat, suffix), { method, headers: { ...headers, 'Content-Type': 'application/json' },
    cache: 'no-store', signal, ...(data === undefined ? {} : { body: JSON.stringify(data) }) });
  return { status: response.status, body: record(await response.json()) ?? {} };
}
export async function ensureLiveSession(chat: string, syncId: string, signal: AbortSignal): Promise<LiveSessionView> {
  const result = await request(chat, '', 'POST', { syncId }, signal);
  const session = decodeLiveSession(result.body.session);
  if (result.status !== 200 || !session || session.syncId !== syncId) throw new Error('live_session_unavailable');
  return session;
}
/** Creates/retrieves the server's one stable chat entry for this Sync. No local-storage identity. */
export async function openLiveSyncConversation(syncId: string): Promise<string> {
  const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
  if (!uuid.test(syncId)) throw new Error('sync_invalid');
  const headers = await authHeaders();
  if (!headers.Authorization) throw new Error('unauthorized');
  const entryBase = `${API_BASE}/api/subscriber-chat/syncs/${encodeURIComponent(syncId)}`;
  let response = await fetch(`${entryBase}/portal-entry`, {
    method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: '{}', cache: 'no-store',
  });
  let result = record(await response.json().catch(() => null));
  // Only the explicit feature-unavailable contract permits the legacy path.
  // Foreign runs, malformed responses and transient failures never fall back.
  if(response.status === 404 && result?.error === 'portal_sync_entry_unavailable'){
    response = await fetch(`${entryBase}/codex-entry`, {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: '{}', cache: 'no-store',
    });
    result = record(await response.json().catch(() => null));
  }
  if (!response.ok || !result || result.syncId !== syncId || typeof result.chatSessionId !== 'string' || !uuid.test(result.chatSessionId))
    throw new Error('live_entry_unavailable');
  return `subscriber-chat:sync:${result.chatSessionId}:${syncId}`;
}
export async function readLiveEvents(chat: string, sessionId: string, after: string, signal: AbortSignal) {
  const result = await request(chat, `/${encodeURIComponent(sessionId)}/events?after=${encodeURIComponent(after)}`, 'GET', undefined, signal);
  const events = decodeLiveEvents(result.body.events); const session = decodeLiveSession(result.body.session);
  const next = result.body.cursor;
  if (result.status !== 200 || !events || !session || session.id !== sessionId || !cursor(next)
    || BigInt(next) < BigInt(after) || events.some((event: LiveEvent) => BigInt(event.cursor) <= BigInt(after) || BigInt(event.cursor) > BigInt(next)))
    throw new Error('live_events_unverified');
  return { events, cursor: next, session };
}
export async function readLiveUsage(chat: string, sessionId: string, after: string | null, signal: AbortSignal): Promise<LiveUsagePage> {
  const result = await request(chat, `/${encodeURIComponent(sessionId)}/usage${after ? `?after=${encodeURIComponent(after)}` : ''}`, 'GET', undefined, signal);
  if ([401, 403, 404].includes(result.status)) throw new LiveUsageAccessRefused();
  const parsed = liveUsagePageSchema.safeParse(result.body);
  if (result.status !== 200 || !parsed.success || parsed.data.sessionId !== sessionId) throw new Error('live_usage_unverified');
  return parsed.data;
}
export class LiveUsageAccessRefused extends Error {
  constructor() { super('live_usage_access_refused'); }
}
export async function sendLiveMessage(chat: string, sessionId: string, messageKey: string, text: string) {
  const result = await request(chat, `/${encodeURIComponent(sessionId)}/messages`, 'POST', { messageKey, text });
  if ([200,409].includes(result.status)) {
    const admission = result.body.admission;
    if (['claimed','existing','busy','budget_exhausted','conflict'].includes(admission as string)) {
      const turn = record(result.body.turn);
      return { admission: admission as string, uncertain: turn?.state === 'uncertain' || turn?.state === 'pending' };
    }
  }
  throw new Error('live_message_unverified');
}
export async function stopLiveSession(chat: string, sessionId: string): Promise<void> {
  const result = await request(chat, `/${encodeURIComponent(sessionId)}/cancel`, 'POST', {});
  if (result.status !== 200 || result.body.state !== 'cancel_requested') throw new Error('live_cancel_unverified');
}
export async function topUpLiveSession(chat: string, sessionId: string, consentId: string, additionalMicros: number): Promise<LiveSessionView> {
  const result = await request(chat, `/${encodeURIComponent(sessionId)}/budget`, 'POST', { consentId, additionalMicros, confirmed: true });
  const session = decodeLiveSession(result.body.session);
  if (result.status === 403 && result.body.error === 'live_codex_budget_limit') throw new LiveBudgetRefused();
  if (result.status !== 200 || !session || session.id !== sessionId) throw new Error('live_budget_unverified');
  return session;
}
/** This specific response is issued before this request can write a consent. */
export class LiveBudgetRefused extends Error {
  constructor() { super('live_codex_budget_limit'); }
}
